/**
 * buildPhotoContext — pure function, no React dependencies.
 *
 * Derives the city list, attraction exclusion list, and active filters to
 * send to /api/suggest for photo-feed, based on which tab triggered photo
 * mode and the current application data.
 *
 * @param {object} p
 * @param {string}   p.screen          - 'input' | 'itinerary' | 'saved'
 * @param {string}   [p.cityOverride]  - single city name (city-tile click)
 * @param {string[]} [p.filtersOverride] - explicit filter list (overrides attractionFilters)
 * @param {object[]} p.cities          - Plan tab city objects  { name, attractions[] }
 * @param {object}   [p.itinerary]     - loaded itinerary       { days[] }
 * @param {object[]} p.snips           - saved snips            { type, name }
 * @param {object[]} p.savedTrips      - saved trips            { days[] }
 * @param {string[]} p.attractionFilters - active filter labels (e.g. ['Culture'])
 * @returns {{ ctxCities: string[], ctxAttractions: string[], ctxFilters: string[] }}
 */
export const buildPhotoContext = ({
  screen,
  cityOverride,
  filtersOverride,
  cities = [],
  itinerary = null,
  snips = [],
  savedTrips = [],
  attractionFilters = [],
}) => {
  const ctxCities = [];
  const ctxAttractions = [];

  if (cityOverride) {
    ctxCities.push(cityOverride);
  } else if (screen === 'input') {
    cities.forEach(c => {
      ctxCities.push(c.name);
      (c.attractions || []).forEach(a => ctxAttractions.push(a.name || a));
    });
  } else if (screen === 'itinerary' && itinerary) {
    itinerary.days.forEach(d => {
      if (d.city) ctxCities.push(d.city);
      (d.activities || []).forEach(a => { if (!a.type) ctxAttractions.push(a.name); });
    });
  } else if (screen === 'saved') {
    snips.forEach(s => s.type === 'city' ? ctxCities.push(s.name) : ctxAttractions.push(s.name));
    // Always include cities from all saved trips so photo mode reflects the full trip
    // history, not just whichever single city the user most recently snipped.
    // Also collect all saved itinerary activities so they're excluded from suggestions.
    const citySet = new Set(ctxCities.map(c => c.toLowerCase()));
    const attrSet = new Set(ctxAttractions.map(n => n.toLowerCase()));
    savedTrips.forEach(t => (t.days || []).forEach(d => {
      if (d.city && !citySet.has(d.city.toLowerCase())) {
        ctxCities.push(d.city);
        citySet.add(d.city.toLowerCase());
      }
      (d.activities || []).forEach(a => {
        if (!a.type && a.name && !attrSet.has(a.name.toLowerCase())) {
          ctxAttractions.push(a.name);
          attrSet.add(a.name.toLowerCase());
        }
      });
    }));
  }

  // Fallback: when the current tab has no city context, pull cities from all saved trips
  if (ctxCities.length === 0) {
    savedTrips.forEach(t => (t.days || []).forEach(d => { if (d.city) ctxCities.push(d.city); }));
  }

  const ctxFilters = filtersOverride !== undefined
    ? filtersOverride.filter(l => l !== 'All')
    : attractionFilters.filter(l => l !== 'All');

  return { ctxCities, ctxAttractions, ctxFilters };
};

// Uses average of range durations for capacity planning ("2-3 hours" → 2.5h).
// Note: calculateNextTime uses the lower bound for display — intentional conservative display.
const durationToHours = (durationStr) => {
  const hourMatch = durationStr.match(/(\d+\.?\d*)\s*-?\s*(\d+\.?\d*)?\s*h/i);
  if (hourMatch) {
    const first = parseFloat(hourMatch[1]);
    const second = hourMatch[2] ? parseFloat(hourMatch[2]) : first;
    return (first + second) / 2;
  }
  return 2;
};

const minutesToTimeStr = (totalMinutes) => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours > 12 ? hours - 12 : (hours === 0 ? 12 : hours);
  return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
};

export const buildItinerary = (cities) => {
  const MAX_HOURS_PER_DAY = 8;
  const days = [];
  let currentDayNumber = 1;

  cities.forEach((city) => {
    if (city.attractions.length === 0) return;

    let currentDayActivities = [];
    let currentDayHours = 0;
    let currentTime = 9 * 60;

    city.attractions.forEach((attr) => {
      const attractionName = typeof attr === 'string' ? attr : attr.name;
      const attractionDuration = typeof attr === 'string' ? '2-3 hours' : attr.duration;
      const durationHours = durationToHours(attractionDuration);

      if (currentDayHours + durationHours > MAX_HOURS_PER_DAY && currentDayActivities.length > 0) {
        days.push({
          id: `day-${currentDayNumber}`,
          day: currentDayNumber++,
          city: city.name,
          activities: currentDayActivities,
          expanded: true
        });
        currentDayActivities = [];
        currentDayHours = 0;
        currentTime = 9 * 60;
      }

      currentDayActivities.push({
        id: `activity-${currentDayNumber}-${currentDayActivities.length}-${Date.now()}`,
        time: minutesToTimeStr(currentTime),
        name: attractionName,
        duration: attractionDuration,
        cityName: city.name,
        ...(typeof attr === 'object' ? {
          placeId: attr.placeId || null,
          fullName: attr.fullName || null,
          photoRefs: attr.photoRefs || [],
          rating: attr.rating ?? null,
          ratingCount: attr.ratingCount ?? null,
          types: attr.types || [],
          description: attr.description || null,
        } : {}),
      });

      currentDayHours += durationHours;
      currentTime += durationHours * 60;
    });

    if (currentDayActivities.length > 0) {
      days.push({
        id: `day-${currentDayNumber}`,
        day: currentDayNumber++,
        city: city.name,
        activities: currentDayActivities,
        expanded: true
      });
    }
  });

  return {
    name: days.length > 0 ? `${cities[0].name} Trip` : 'Trip',
    days
  };
};

export const estimateDuration = (types) => {
  if (!types || types.length === 0) return '2-3 hours';
  if (types.some(t => ['amusement_park'].includes(t))) return '4-6 hours';
  if (types.some(t => ['natural_feature', 'campground'].includes(t))) return '2-4 hours';
  if (types.some(t => ['park'].includes(t))) return '1-2 hours';
  if (types.some(t => ['church', 'hindu_temple', 'mosque', 'synagogue', 'place_of_worship'].includes(t))) return '30-60 minutes';
  if (types.some(t => ['museum', 'art_gallery', 'aquarium', 'zoo'].includes(t))) return '2-3 hours';
  if (types.some(t => ['restaurant', 'cafe', 'bar'].includes(t))) return '1-1.5 hours';
  if (types.some(t => ['shopping_mall', 'store'].includes(t))) return '2-3 hours';
  return '2-3 hours';
};

export const calculateNextTime = (timeStr, durationStr) => {
  const timeToMinutes = (time) => {
    const match = time.match(/(\d+):(\d+)\s*(AM|PM)/i);
    if (!match) return 540;
    let hours = parseInt(match[1]);
    const minutes = parseInt(match[2]);
    const period = match[3].toUpperCase();
    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
    return hours * 60 + minutes;
  };

  const minutesToTime = (totalMinutes) => {
    const wrapped = totalMinutes % (24 * 60);
    let hours = Math.floor(wrapped / 60);
    const mins = wrapped % 60;
    const period = hours >= 12 ? 'PM' : 'AM';
    if (hours > 12) hours -= 12;
    if (hours === 0) hours = 12;
    return `${hours}:${mins.toString().padStart(2, '0')} ${period}`;
  };

  const durationToMinutes = (duration) => {
    if (!duration) return 60;
    // For ranges like "2-3 hours", take the first (lower) number
    const rangeMatch = duration.match(/^(\d+\.?\d*)\s*-/);
    if (rangeMatch) return parseFloat(rangeMatch[1]) * 60;
    const hourMatch = duration.match(/(\d+\.?\d*)\s*h/i);
    const minMatch = duration.match(/(\d+)\s*m/i);
    let totalMinutes = 0;
    if (hourMatch) totalMinutes += parseFloat(hourMatch[1]) * 60;
    if (minMatch) totalMinutes += parseInt(minMatch[1]);
    return totalMinutes || 60;
  };

  return minutesToTime(timeToMinutes(timeStr) + durationToMinutes(durationStr));
};

/**
 * Recalculates activity times for a single itinerary day.
 * - Starts at 9:00 AM
 * - Activities with pinnedTime:true act as anchors (kept as-is, cursor jumps past them)
 * - Hotels always get time:undefined (they have no scheduled slot)
 * - Once the cursor hits 7:00 PM, remaining free activities share the last scheduled time
 *   (overflow = "pick one of these" alternatives)
 * - Range durations ("4-6 hours") use the lower bound
 */
export const recalculateTimes = (activities) => {
  if (!activities || activities.length === 0) return activities || [];
  const EOD = 19 * 60; // 7:00 PM

  const timeToMins = (t) => {
    if (!t) return 9 * 60;
    const m = t.match(/(\d+):(\d+)\s*(AM|PM)/i);
    if (m) {
      let h = parseInt(m[1]); const mins = parseInt(m[2]); const pm = m[3].toUpperCase() === 'PM';
      if (pm && h !== 12) h += 12; if (!pm && h === 12) h = 0;
      return h * 60 + mins;
    }
    const h24 = t.match(/^(\d{1,2}):(\d{2})$/);
    if (h24) return parseInt(h24[1]) * 60 + parseInt(h24[2]);
    return 9 * 60;
  };
  const minsToTime = (mins) => {
    const h = Math.floor(((mins % 1440) + 1440) % 1440 / 60), m = mins % 60;
    const p = h >= 12 ? 'PM' : 'AM', hr = h % 12 || 12;
    return `${hr}:${m.toString().padStart(2, '0')} ${p}`;
  };
  const durToMins = (d) => {
    if (!d) return 60;
    const range = d.match(/^(\d+\.?\d*)\s*-/); if (range) return parseFloat(range[1]) * 60;
    const h = d.match(/(\d+\.?\d*)\s*h/i), m = d.match(/(\d+)\s*m/i);
    let t = 0; if (h) t += parseFloat(h[1]) * 60; if (m) t += parseInt(m[1]);
    return t || 60;
  };

  let cursor = 9 * 60;
  let overflowTime = null;

  return activities.map(a => {
    if (a.type === 'hotel') return { ...a, time: undefined };

    if (a.pinnedTime) {
      cursor = timeToMins(a.time) + durToMins(a.duration);
      if (cursor >= EOD && overflowTime === null) overflowTime = timeToMins(a.time);
      return a;
    }

    if (overflowTime !== null) return { ...a, time: minsToTime(overflowTime) };

    const assigned = cursor;
    cursor += durToMins(a.duration);
    if (cursor >= EOD) overflowTime = assigned;
    return { ...a, time: minsToTime(assigned) };
  });
};

export const MAJOR_TOURIST_CITIES = [
  'paris', 'london', 'new york', 'tokyo', 'rome', 'barcelona',
  'amsterdam', 'prague', 'istanbul', 'dubai', 'hong kong', 'singapore',
  'los angeles', 'san francisco', 'chicago', 'las vegas', 'miami',
  'sydney', 'melbourne', 'bangkok', 'bali', 'phuket',
  'venice', 'florence', 'athens', 'lisbon', 'madrid', 'berlin',
  'vienna', 'budapest', 'copenhagen', 'stockholm', 'oslo',
  'edinburgh', 'dublin', 'reykjavik', 'cairo', 'marrakech',
];

export const isMajorTouristCity = (cityName) => {
  const lower = (cityName || '').toLowerCase();
  return MAJOR_TOURIST_CITIES.some(major => lower.includes(major));
};
